import { createContext, useContext } from 'react'

import type { IOptionItem } from '~/client/src/shared/components/OptionsList/types'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import { IQuickFilters } from '~/client/src/shared/hooks/useResourceQuickFilters'
import { GroupingState } from '~/client/src/shared/hooks/useTableGrouping'
import { IResourceFieldCatalog } from '~/client/src/shared/resources/catalog/types'
import { IResourceRuntimeContext } from '~/client/src/shared/resources/definitions/types'
import type { IResourceControlCommands } from '~/client/src/shared/resources/domain/resourceControls'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'
import { IResourceFieldProjector } from '~/client/src/shared/resources/projection/types'

import { TableFiltersProps } from '../../TableFilters/types'
import { TableGroupSortProps } from '../../TableGroupSort/types'
import {
  FilterFacet,
  IResourceColumn,
  IResourceRecord,
  ISourceMap,
} from '../types'

export interface ResourceState {
  records: IResourceRecord[]
  filteredRecords: IResourceRecord[]
  selectedIds: Set<string>
  selectedRecordId: string | null
  /** A resource just created here, open by its id before its record arrives. */
  pendingCreatedRecordId: string | null
  chosenTemplateId: string | null
  isBulkEditPanelOpen: boolean
  appliedFilters: { [facetKey: string]: Set<string> }
  searchKey: string
  grouping: string | null
  sorting: { key: string; order: SortOrder }
  collapsedCategories: Map<string, boolean>
  isAllCollapsed: boolean
}

export interface ResourceActions {
  selectRecord: (id: string) => void
  closeEditPanel: () => void
  createNew: () => void
  setChosenTemplateId: (templateId: string | null) => void
  openBulkEditPanel: () => void
  closeBulkEditPanel: () => void
  toggleSelection: (id: string) => void
  toggleCategorySelection: (ids: string[]) => void
  setSelection: (ids: Set<string>) => void
  clearSelection: () => void
  applyFilter: (facetKey: string, ids: string[]) => void
  resetFilters: () => void
  setSearchKey: (key: string) => void
  setAppliedFilters: (filters: { [facetKey: string]: Set<string> }) => void
  setGrouping: (key: string | null) => void
  setSorting: (key: string, order: SortOrder) => void
  toggleCategory: (categoryId: string) => void
  collapseAll: () => void
  expandAll: () => void
  save: (data: Record<string, any>) => Promise<void>
  bulkEditSelected: (changes: Record<string, any>) => Promise<void>
  deleteSelected: () => Promise<void>
  deleteRecords: (ids: string[]) => Promise<void>
}

export interface ResourceMeta {
  projectId: string
  resourceTypeId: string
  columns: IResourceColumn[]
  configuredColumns: IResourceColumn[]
  defaultColumns: IResourceColumn[]
  columnWidths: Map<string, number>
  onColumnResize: (key: string, width: number) => void
  fixedColumnCount: number
  updateColumnsConfiguration: (
    columns: Pick<IResourceColumn, 'dataKey' | 'isHidden' | 'isFixed'>[],
  ) => void
  resetColumnsConfiguration: () => void
  facets: FilterFacet[]
  sourceMapByFacet: { [key: string]: ISourceMap }
  getOptionName: (optionId: string, facetKey: string) => string
  hasActiveFilters: boolean
  groupingOptions: IOptionItem[]
  sortingOptions: IOptionItem[]
  excludedFromSortKeys: string[]
  isLoading: boolean
  selectedCount: number
  filteredCount: number
  isSelected: (id: string) => boolean
  isCategorySelected: (ids: string[]) => boolean
  onCategoryCheckboxToggle: (category: { idsToSelect?: string[] }) => void
  groupSortProps: TableGroupSortProps
  filterProps: TableFiltersProps
  groupingControls: GroupingState
  runtimeContext: IResourceRuntimeContext
  fieldProjector: IResourceFieldProjector
  fieldCatalog: IResourceFieldCatalog
  quickFilters: IQuickFilters
  schema: ResourceSchema
  /** The control commands for THIS resource type, resolved by the provider. */
  controlCommands: IResourceControlCommands
}

export interface ResourceContextValue {
  state: ResourceState
  actions: ResourceActions
  meta: ResourceMeta
}

export const ResourceContext = createContext<ResourceContextValue | null>(null)

export function useResource(): ResourceContextValue {
  const context = useContext(ResourceContext)
  if (!context) {
    throw new Error('useResource must be used within Resource.Provider')
  }
  return context
}

export function useResourceState(): ResourceState {
  return useResource().state
}

export function useResourceActions(): ResourceActions {
  return useResource().actions
}

export function useResourceMeta(): ResourceMeta {
  return useResource().meta
}
